import type { RatingValue } from '@risksmart-app/components/src/hooks/useRating';
import { getColorStyles } from '@risksmart-app/components/src/utils/colours';

import { ConformanceIndicatorRating } from './types';

export type IndicatorReferenceValues = {
  upperTolerance?: null | number;
  lowerTolerance?: null | number;
  upperAppetite?: null | number;
  lowerAppetite?: null | number;
};

export type IndicatorReferenceLine = {
  value: number;
  color: string;
  label: string;
};

type IndicatorRatingResolvers = {
  getColorClass: (value: RatingValue) => null | string;
  getLabel: (value: RatingValue) => string;
};

/**
 * Shared construction of an indicator's tolerance/appetite reference lines —
 * the single source of truth for the line colours, labels, value selection and
 * ordering used by both the indicator results-tab chart (`ResultsChart`, via
 * Cloudscape threshold series) and the dashboard line-chart widget
 * (`IndicatorResultsOverTime`, via Highcharts plotLines). Tolerance bounds use
 * the "Outside" conformance rating, appetite bounds the "Outside appetite"
 * rating. Nil bounds are skipped. Pass the resolvers from
 * `useRating('indicator_conformance_status')`; each consumer maps the returned
 * lines into its own chart library's shape.
 */
export const buildIndicatorReferenceLines = (
  {
    upperTolerance,
    lowerTolerance,
    upperAppetite,
    lowerAppetite,
  }: IndicatorReferenceValues,
  { getColorClass, getLabel }: IndicatorRatingResolvers
): IndicatorReferenceLine[] =>
  [
    { value: upperTolerance, rating: ConformanceIndicatorRating.Outside },
    {
      value: upperAppetite,
      rating: ConformanceIndicatorRating.OutsideAppetite,
    },
    {
      value: lowerAppetite,
      rating: ConformanceIndicatorRating.OutsideAppetite,
    },
    { value: lowerTolerance, rating: ConformanceIndicatorRating.Outside },
  ].flatMap(({ value, rating }) =>
    value == null
      ? []
      : [
          {
            value,
            color: getColorStyles(getColorClass(rating) ?? '').backgroundColor,
            label: getLabel(rating),
          },
        ]
  );
